import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExpandModal from '../components/ExpandModal';
import WearTodayModal from '../components/WearTodayModal';
import ReviewWearModal from '../components/ReviewWearModal';
import ChecklistCard from '../components/ChecklistCard';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

// ── Experience level helper ───────────────────────────────────────────────────
const EXPERIENCE_LEVELS = ['new', 'beginner', 'intermediate', 'expert'];

const getEffectiveExperience = (libraryCount, onboardingExperience) => {
  let fromLibrary;
  if (libraryCount === 0) fromLibrary = 'new';
  else if (libraryCount <= 3) fromLibrary = 'beginner';
  else if (libraryCount <= 9) fromLibrary = 'intermediate';
  else fromLibrary = 'expert';

  const libraryIndex = EXPERIENCE_LEVELS.indexOf(fromLibrary);
  const onboardingIndex = EXPERIENCE_LEVELS.indexOf(onboardingExperience || 'new');
  return EXPERIENCE_LEVELS[Math.max(libraryIndex, onboardingIndex)];
};

// ── Notes nudge banner ────────────────────────────────────────────────────────
const NotesNudge = ({ onNavigate, isMobile }) => {
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(
    localStorage.getItem('notes_nudge_dismissed') === 'true'
  );

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => {
      localStorage.setItem('notes_nudge_dismissed', 'true');
      setDismissed(true);
    }, 300);
  };

  if (dismissed) return null;

  return (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '12px' : '0',
      padding: isMobile ? '16px 20px' : '16px 28px',
      border: '1px solid #2a2a2a', borderLeft: '2px solid #c9a96e',
      background: 'rgba(201,169,110,0.04)', marginBottom: '32px',
      opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease',
    }}>
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '14px' }}>
        <span style={{ color: '#c9a96e', fontSize: '0.8rem', flexShrink: 0, marginTop: isMobile ? '2px' : '0' }}>✦</span>
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#a09890', lineHeight: 1.6 }}>
          Want more precise recommendations?{' '}
          <span onClick={() => { onNavigate('profile'); handleDismiss(); }}
            style={{ color: '#c9a96e', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
            Add your favourite notes in preferences
          </span>{' '}to refine your results.
        </p>
      </div>
      <button onClick={handleDismiss}
        style={{ background: 'none', border: 'none', color: '#3a3a3a', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0, padding: 0 }}
        onMouseEnter={e => e.target.style.color = '#665e58'}
        onMouseLeave={e => e.target.style.color = '#3a3a3a'}
      >Dismiss</button>
    </div>
  );
};

// ── Home ──────────────────────────────────────────────────────────────────────
const Home = ({ user, onSignIn, onSignUp, onNavigate, token }) => {
  const isMobile = useIsMobile();
  const [weather, setWeather] = useState(null);
  const [occasion, setOccasion] = useState('casual');
  const [recommendations, setRecommendations] = useState([]);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [weatherError, setWeatherError] = useState('');
  const [expandRecs, setExpandRecs] = useState([]);
  const [selectedExpand, setSelectedExpand] = useState(null);
  const [wearItem, setWearItem] = useState(null);
  const [todayEntry, setTodayEntry] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [hasLibrary, setHasLibrary] = useState(null);
  const [libraryCount, setLibraryCount] = useState(0);
  const [hasNotes, setHasNotes] = useState(true);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [featuredFragrances, setFeaturedFragrances] = useState([]);
  const [onboardingExperience, setOnboardingExperience] = useState(null);
  const [allFeatured, setAllFeatured] = useState([]);
  const [selectedFeatured, setSelectedFeatured] = useState(null);
  const [featuredOffset, setFeaturedOffset] = useState(0);
  const [profileLoading, setProfileLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) getLocation(); }, [user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && token) {
      fetchHomeData();
      if (localStorage.getItem(`checklist_dismissed_${user.id}`) !== 'true') {
        setShowChecklist(true);
      }
    }
  }, [user]);

  const fetchHomeData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [libraryRes, wishlistRes, profileRes] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_API_URL}/api/library`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/wishlist`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/profile`, { headers }),
      ]);

      const library = libraryRes.status === 'fulfilled' ? libraryRes.value.data : [];
      const hasItems = Array.isArray(library) && library.length > 0;
      const count = Array.isArray(library) ? library.length : 0;
      setHasLibrary(hasItems);
      setLibraryCount(count);

      if (wishlistRes.status === 'fulfilled') {
        setWishlistItems(Array.isArray(wishlistRes.value.data) ? wishlistRes.value.data.slice(0, 3) : []);
      }

      if (profileRes.status === 'fulfilled') {
        const notes = profileRes.value.data?.profile?.favourite_notes || [];
        const experience = profileRes.value.data?.profile?.experience || null;
        setHasNotes(notes.length > 0);
        setOnboardingExperience(experience);
      }

      if (!hasItems) {
        try {
          const recsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/fragrances/recommendations`, { headers });
          const recs = Array.isArray(recsRes.data) ? recsRes.data : [];
          setFeaturedFragrances(recs.slice(0, 3));
          setAllFeatured(recs); // store all for refresh
        } catch (err) { console.error(err); }
      } else {
        fetchExpandRecs();
      }

      fetchTodayEntry();
    } catch (err) { console.error(err); }
    setProfileLoading(false);
  };

  const fetchExpandRecs = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/fragrances/expand`, { headers: { Authorization: `Bearer ${token}` } });
      setExpandRecs(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchTodayEntry = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/journal/today`, { headers: { Authorization: `Bearer ${token}` } });
      setTodayEntry(res.data);
    } catch (err) { console.error(err); }
  };

  const handleRefreshFeatured = async () => {
    if (allFeatured.length <= 3) return;
    const nextOffset = featuredOffset + 3;

    // If we've reached the end of the pool, fetch a fresh set
    if (nextOffset >= allFeatured.length) {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const recsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/fragrances/recommendations`, { headers });
        const fresh = Array.isArray(recsRes.data) ? recsRes.data : [];
        setAllFeatured(fresh);
        setFeaturedOffset(0);
        setFeaturedFragrances(fresh.slice(0, 3));
      } catch (err) { console.error(err); }
      return;
    }

    setFeaturedOffset(nextOffset);
    setFeaturedFragrances(allFeatured.slice(nextOffset, nextOffset + 3));
  };

  const getLocation = (occasionOverride) => {
    const activeOccasion = occasionOverride || occasion;
    setLoadingWeather(true);
    setWeatherError('');
    if (!navigator.geolocation) { setWeatherError('Geolocation not supported by your browser'); setLoadingWeather(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/weather?lat=${latitude}&lon=${longitude}&occasion=${activeOccasion}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setWeather(res.data.weather);
          setRecommendations(res.data.recommendations);
        } catch (err) { setWeatherError('Could not load recommendations'); }
        setLoadingWeather(false);
      },
      (err) => {
        if (err.code === 1) setWeatherError('Allow location access to get personalised recommendations');
        else if (err.code === 2) setWeatherError('Your location is currently unavailable');
        else setWeatherError('Location request timed out — tap Retry to try again');
        setLoadingWeather(false);
      },
      { maximumAge: 0, timeout: 10000, enableHighAccuracy: false }
    );
  };

  const showNotesNudge = libraryCount >= 3
    && !hasNotes
    && localStorage.getItem('notes_nudge_dismissed') !== 'true';

  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'Good Morning' : hour < 18 ? 'Good Afternoon' : 'Good Evening';
  const experience = getEffectiveExperience(libraryCount, onboardingExperience);
  const isNewUser = experience === 'new';

  const styles = {
    container: { minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: isMobile ? '100px 16px 48px' : '120px 24px 48px', position: 'relative', overflow: 'hidden' },
    background: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(ellipse at 50% 50%, #1a1408 0%, #0a0a0a 70%)', zIndex: 0 },
    content: { position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '700px', width: '100%', padding: isMobile ? '0 8px' : '0' },
    dashboard: { position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '900px', width: '100%' },
    greeting: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '16px' },
    dashTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2.2rem' : 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 300, color: '#f5f0eb', marginBottom: isMobile ? '28px' : '48px', lineHeight: 1.1 },
    italic: { fontStyle: 'italic', color: '#c9a96e' },
    weatherBar: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: isMobile ? '16px' : '24px', marginBottom: '32px', padding: isMobile ? '14px 20px' : '16px 32px', border: '1px solid #2a2a2a', background: '#111111' },
    weatherCity: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e' },
    weatherTemp: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 300, color: '#f5f0eb' },
    weatherCondition: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#a09890', textTransform: 'capitalize' },
    sectionLabel: { fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '20px', textAlign: 'left' },
    recGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a', marginBottom: '32px' },
    recCard: { background: '#111111', padding: isMobile ? '24px 20px' : '32px', textAlign: 'left', cursor: 'pointer', transition: 'background 0.3s ease' },
    recBrand: { fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' },
    recName: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '12px' },
    recAccord: { display: 'inline-block', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a09890', border: '1px solid #2a2a2a', padding: '3px 8px', marginBottom: '12px' },
    recBottle: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#665e58' },
    navCards: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a' },
    navCard: { background: '#0e0e0e', padding: '24px 28px', cursor: 'pointer', transition: 'background 0.3s ease', textAlign: 'left' },
    navCardIcon: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', color: '#665e58', marginBottom: '8px', fontStyle: 'italic' },
    navCardTitle: { fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#a09890' },
    loadingText: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 300, color: '#665e58', fontStyle: 'italic', marginBottom: '32px' },
    errorText: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: '#665e58', marginBottom: '32px' },
    retryBtn: { background: 'none', border: '1px solid #2a2a2a', color: '#a09890', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '8px 20px', cursor: 'pointer', marginLeft: '12px' },
    eyebrow: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '32px' },
    title: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? 'clamp(2.8rem, 12vw, 4rem)' : 'clamp(3rem, 8vw, 6rem)', fontWeight: 300, lineHeight: 1.05, color: '#f5f0eb', marginBottom: '32px' },
    subtitle: { fontFamily: 'Jost, sans-serif', fontSize: isMobile ? '0.85rem' : '0.95rem', fontWeight: 300, lineHeight: 1.8, color: '#a09890', marginBottom: '56px', maxWidth: '480px', margin: '0 auto 40px' },
    buttons: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px', justifyContent: 'center', alignItems: 'center' },
    primaryBtn: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#0a0a0a', background: '#c9a96e', border: 'none', padding: '16px 40px', cursor: 'pointer', width: isMobile ? '100%' : 'auto' },
    secondaryBtn: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c9a96e', background: 'none', border: '1px solid #2a2a2a', padding: '16px 40px', cursor: 'pointer', width: isMobile ? '100%' : 'auto' },
    divider: { width: '1px', height: '80px', background: 'linear-gradient(to bottom, transparent, #c9a96e, transparent)', margin: '80px auto 0' },
    ctaBanner: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '16px' : '0', padding: isMobile ? '24px 20px' : '28px 40px', border: '1px solid #2a2a2a', background: '#0e0e0e', marginBottom: '32px' },
  };

  if (user) {
    return (
      <div style={styles.container}>
        <div style={styles.background} />
        <div style={styles.dashboard}>
          <p style={styles.greeting}>{timeOfDay}</p>
          <h1 style={styles.dashTitle}>
            Welcome back,<br />
            <span style={styles.italic}>{user.username.charAt(0).toUpperCase() + user.username.slice(1)}</span>
          </h1>

          {/* ── Inline checklist card ── */}
          {/* Pass onboardingExperience directly — the checklist tier is locked to what
              the user selected during onboarding and must not jump when they add library
              items mid-session. The dynamically computed `experience` is used only for
              the rest of the dashboard UI below (CTAs, featured sections, etc). */}
          {showChecklist && onboardingExperience !== null && (
            <ChecklistCard
              token={token}
              onNavigate={onNavigate}
              isMobile={isMobile}
              experience={onboardingExperience}
              onDismiss={() => {
                localStorage.setItem(`checklist_dismissed_${user.id}`, 'true');
                setShowChecklist(false);
              }}
            />
          )}

          {/* ── Notes nudge ── */}
          {showNotesNudge && (
            <NotesNudge onNavigate={onNavigate} isMobile={isMobile} />
          )}

          {/* ── Empty library — New user ── */}
          {!profileLoading && hasLibrary === false && isNewUser && !showChecklist && (
            <>
              <div style={styles.ctaBanner}>
                <div>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' }}>Start Here</p>
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 300, color: '#f5f0eb', lineHeight: 1.2 }}>
                    Find your first <em style={{ color: '#c9a96e' }}>fragrance</em>
                  </p>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#665e58', marginTop: '8px', lineHeight: 1.7 }}>
                    Browse hundreds of fragrances, save the ones you love to your wishlist, and buy with confidence.
                  </p>
                </div>
                <button onClick={() => onNavigate('discover')}
                  style={{ flexShrink: 0, fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#0a0a0a', background: '#c9a96e', border: 'none', padding: '14px 32px', cursor: 'pointer', alignSelf: isMobile ? 'stretch' : 'auto', textAlign: 'center' }}
                >Browse Discover</button>
              </div>

              {wishlistItems.length > 0 && (
                <>
                  <p style={styles.sectionLabel}>Your Wishlist</p>
                  <div style={styles.recGrid}>
                    {wishlistItems.map(item => (
                      <div key={item.id} style={styles.recCard} onMouseEnter={e => e.currentTarget.style.background = '#161616'} onMouseLeave={e => e.currentTarget.style.background = '#111111'}>
                        <p style={styles.recBrand}>{item.brand}</p>
                        <p style={styles.recName}>{item.name}</p>
                        {item.main_accord && <span style={styles.recAccord}>{item.main_accord}</span>}
                        <p style={{ ...styles.recBottle, color: item.price_gbp ? '#c9a96e' : '#3a3a3a' }}>{item.price_gbp ? `£${item.price_gbp}` : '—'}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {featuredFragrances.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: wishlistItems.length > 0 ? '16px' : '0' }}>
                    <p style={{ ...styles.sectionLabel, marginBottom: 0 }}>Fragrances to Explore</p>
                    {allFeatured.length > 3 && (
                      <button
                        onClick={handleRefreshFeatured}
                        style={{ background: 'none', border: '1px solid #2a2a2a', color: '#665e58', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '6px 14px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a96e'; e.currentTarget.style.color = '#c9a96e'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#665e58'; }}
                      >↻ Refresh</button>
                    )}
                  </div>
                  <div style={styles.recGrid}>
                    {featuredFragrances.map(item => (
                      <div key={item.id} style={styles.recCard} onClick={() => setSelectedFeatured(item)} onMouseEnter={e => e.currentTarget.style.background = '#161616'} onMouseLeave={e => e.currentTarget.style.background = '#111111'}>
                        <p style={styles.recBrand}>{item.brand}</p>
                        <p style={styles.recName}>{item.name}</p>
                        {item.main_accord && <span style={styles.recAccord}>{item.main_accord}</span>}
                        <p style={{ ...styles.recBottle, color: '#c9a96e', fontStyle: 'italic' }}>Explore in Discover →</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Empty library — Beginner/Intermediate/Expert ── */}
          {!profileLoading && hasLibrary === false && !isNewUser && !showChecklist && (
            <>
              <div style={styles.ctaBanner}>
                <div>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' }}>Get Started</p>
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 300, color: '#f5f0eb', lineHeight: 1.2 }}>
                    Add your collection <em style={{ color: '#c9a96e' }}>to Flakon</em>
                  </p>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#665e58', marginTop: '8px', lineHeight: 1.7 }}>
                    Log your bottles to unlock daily recommendations, wear tracking, and collection insights.
                  </p>
                </div>
                <button onClick={() => onNavigate('library')}
                  style={{ flexShrink: 0, fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#0a0a0a', background: '#c9a96e', border: 'none', padding: '14px 32px', cursor: 'pointer', alignSelf: isMobile ? 'stretch' : 'auto', textAlign: 'center' }}
                >Add to Library</button>
              </div>

              {experience === 'beginner' && featuredFragrances.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <p style={{ ...styles.sectionLabel, marginBottom: 0 }}>Fragrances to Explore</p>
                    {allFeatured.length > 3 && (
                      <button
                        onClick={handleRefreshFeatured}
                        style={{ background: 'none', border: '1px solid #2a2a2a', color: '#665e58', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '6px 14px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a96e'; e.currentTarget.style.color = '#c9a96e'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#665e58'; }}
                      >↻ Refresh</button>
                    )}
                  </div>
                  <div style={styles.recGrid}>
                    {featuredFragrances.map(item => (
                      <div key={item.id} style={styles.recCard} onClick={() => setSelectedFeatured(item)} onMouseEnter={e => e.currentTarget.style.background = '#161616'} onMouseLeave={e => e.currentTarget.style.background = '#111111'}>
                        <p style={styles.recBrand}>{item.brand}</p>
                        <p style={styles.recName}>{item.name}</p>
                        {item.main_accord && <span style={styles.recAccord}>{item.main_accord}</span>}
                        <p style={{ ...styles.recBottle, color: '#c9a96e', fontStyle: 'italic' }}>Explore in Discover →</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {(experience === 'intermediate' || experience === 'expert') && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a', marginBottom: '32px' }}>
                  {[
                    { icon: 'I', title: 'Track Levels', desc: "Log how full each bottle is and see when you're running low." },
                    { icon: 'II', title: 'Daily Wear', desc: 'Get recommendations from your own collection based on weather and occasion.' },
                    { icon: 'III', title: 'Expand Smarter', desc: "See what's missing from your collection and discover what fills the gaps." },
                  ].map((c, i) => (
                    <div key={i} style={{ background: '#0e0e0e', padding: isMobile ? '24px 20px' : '28px 32px', textAlign: 'left' }}>
                      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', color: '#c9a96e', fontStyle: 'italic', marginBottom: '10px' }}>{c.icon}</p>
                      <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.62rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#f5f0eb', marginBottom: '8px' }}>{c.title}</p>
                      <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.72rem', fontWeight: 300, color: '#4a4a4a', lineHeight: 1.6 }}>{c.desc}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Has library — normal dashboard ── */}
          {hasLibrary === true && (
            <>
              {todayEntry && (
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '12px' : '0', padding: isMobile ? '16px' : '16px 24px', border: '1px solid #2a2a2a', background: '#0e0e0e', marginBottom: '16px' }}>
                  <div>
                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#665e58', marginBottom: '4px' }}>Wearing Today</p>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.3rem', fontWeight: 300, color: '#f5f0eb' }}>{todayEntry.brand} — {todayEntry.name}</p>
                  </div>
                  <button onClick={() => setShowReview(true)} style={{ background: 'none', border: '1px solid #c9a96e', color: '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '10px 20px', cursor: 'pointer', alignSelf: isMobile ? 'stretch' : 'auto', textAlign: 'center' }}>How Did It Do?</button>
                </div>
              )}

              {/* ── Occasion selector ── */}
              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#3a3a3a', marginBottom: '12px' }}>What's the occasion?</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'casual', label: 'Casual' },
                    { key: 'work', label: 'Work' },
                    { key: 'date', label: 'Date' },
                    { key: 'evening', label: 'Evening' },
                    { key: 'sport', label: 'Sport' },
                    { key: 'special', label: 'Special' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => {
                        setOccasion(key);
                        setRecommendations([]);
                        setTimeout(() => getLocation(key), 0);
                      }}
                      style={{
                        background: occasion === key ? 'rgba(201,169,110,0.1)' : 'none',
                        border: `1px solid ${occasion === key ? '#c9a96e' : '#2a2a2a'}`,
                        color: occasion === key ? '#c9a96e' : '#665e58',
                        fontFamily: 'Jost, sans-serif',
                        fontSize: '0.62rem',
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        padding: isMobile ? '8px 14px' : '8px 18px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >{label}</button>
                  ))}
                </div>
              </div>

              {weather && (
                <div style={styles.weatherBar}>
                  <span style={styles.weatherCity}>{weather.city}</span>
                  <span style={styles.weatherTemp}>{weather.temp}°C</span>
                  <span style={styles.weatherCondition}>{weather.condition}</span>
                </div>
              )}

              {loadingWeather && <p style={styles.loadingText}>Finding your perfect scent for today...</p>}
              {weatherError && (
                <p style={styles.errorText}>
                  {weatherError}
                  <button style={styles.retryBtn} onClick={() => getLocation(occasion)}>Retry</button>
                </p>
              )}

              {recommendations.length > 0 && (
                <>
                  <p style={styles.sectionLabel}>Recommended for Today</p>
                  <div style={styles.recGrid}>
                    {recommendations.map(item => (
                      <div key={item.id} style={styles.recCard} onClick={() => setWearItem(item)} onMouseEnter={e => e.currentTarget.style.background = '#161616'} onMouseLeave={e => e.currentTarget.style.background = '#111111'}>
                        <p style={styles.recBrand}>{item.brand}</p>
                        <p style={styles.recName}>{item.name}</p>
                        {item.main_accord && <span style={styles.recAccord}>{item.main_accord}</span>}
                        <p style={styles.recBottle}>{item.bottle_percentage}% remaining</p>
                        {item.reason && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', fontWeight: 300, color: '#c9a96e', fontStyle: 'italic', marginTop: '8px', lineHeight: 1.5 }}>{item.reason}</p>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {expandRecs.length > 0 && (
                <>
                  <p style={{ ...styles.sectionLabel, marginTop: '32px' }}>Expand Your Collection</p>
                  <div style={styles.recGrid}>
                    {expandRecs.map(item => (
                      <div key={item.id} style={styles.recCard} onClick={() => setSelectedExpand(item)} onMouseEnter={e => e.currentTarget.style.background = '#161616'} onMouseLeave={e => e.currentTarget.style.background = '#111111'}>
                        <p style={styles.recBrand}>{item.brand}</p>
                        <p style={styles.recName}>{item.name}</p>
                        {item.main_accord && <span style={styles.recAccord}>{item.main_accord}</span>}
                        <p style={{ ...styles.recBottle, color: '#c9a96e', fontStyle: 'italic' }}>{item.reason}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {selectedExpand && <ExpandModal item={selectedExpand} token={token} onClose={() => setSelectedExpand(null)} onNavigate={onNavigate} />}
        </div>

        {wearItem && <WearTodayModal item={wearItem} token={token} onClose={() => setWearItem(null)} onSuccess={() => { setWearItem(null); getLocation(); fetchTodayEntry(); }} />}
        {showReview && todayEntry && <ReviewWearModal entry={todayEntry} token={token} onClose={() => setShowReview(false)} onSuccess={() => { setShowReview(false); setTodayEntry(null); fetchTodayEntry(); }} />}
        {selectedFeatured && <ExpandModal item={selectedFeatured} token={token} onClose={() => setSelectedFeatured(null)} onNavigate={onNavigate} mode="wishlist" />}
      </div>
    );
  }

  // ── Logged out landing page ───────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
      <div style={styles.background} />

      {/* ── Hero ── */}
      <div style={{ ...styles.container, minHeight: isMobile ? '85vh' : '88vh', paddingBottom: '80px' }}>
        <div style={styles.content}>
          <p style={styles.eyebrow}>Your Fragrance Companion</p>
          <h1 style={styles.title}>Wear the right<br /><span style={styles.italic}>scent</span> every day</h1>
          <p style={styles.subtitle}>Curate your collection. Track every bottle. Let Flakon guide your daily ritual with weather-aware, occasion-perfect recommendations.</p>
          <div style={styles.buttons}>
            <button style={styles.primaryBtn} onClick={onSignUp}>Begin Your Collection</button>
            <button style={styles.secondaryBtn} onClick={onSignIn}>Sign In</button>
          </div>
        </div>
      </div>

      {/* ── Features section — desktop only ── */}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '1100px', padding: isMobile ? '0 16px 80px' : '0 48px 80px', boxSizing: 'border-box' }}>
        {!isMobile && (
          <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: '64px', marginBottom: '64px' }}>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '16px', textAlign: 'center' }}>What Flakon Does</p>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 300, color: '#f5f0eb', textAlign: 'center', marginBottom: '56px', lineHeight: 1.1 }}>
              Everything your fragrance<br /><em style={{ color: '#c9a96e' }}>collection deserves</em>
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1px', background: '#1a1a1a', border: '1px solid #1a1a1a' }}>
              {[
                {
                  icon: 'I',
                  title: 'Daily Recommendations',
                  desc: "Every morning, Flakon looks at the weather, the season, and your collection — then tells you exactly what to wear. No more standing at your shelf wondering.",
                },
                {
                  icon: 'II',
                  title: 'Collection Management',
                  desc: 'Log every bottle you own with size and current level. Track how fast you go through each one and get notified when you\'re running low.',
                },
                {
                  icon: 'III',
                  title: 'Wear Journal',
                  desc: 'Record every wear with occasion, mood, longevity and projection. Build a personal history of your fragrance journey over time.',
                },
                {
                  icon: 'IV',
                  title: 'Discover New Fragrances',
                  desc: 'Browse hundreds of fragrances filtered by accord, season, occasion and budget. Save anything that catches your eye to your wishlist.',
                },
                {
                  icon: 'V',
                  title: 'Expand Your Collection',
                  desc: "Flakon analyses your taste profile and recommends fragrances that fill gaps in your collection — not just what's popular.",
                },
                {
                  icon: 'VI',
                  title: 'Built for Enthusiasts',
                  desc: 'Whether you own 1 bottle or 100, Flakon adapts to your experience level. The more you use it, the smarter it gets.',
                },
              ].map((f, i) => (
                <div key={i} style={{ background: '#0a0a0a', padding: isMobile ? '28px 20px' : '36px 32px' }}>
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', color: '#c9a96e', fontStyle: 'italic', marginBottom: '14px', lineHeight: 1 }}>{f.icon}</p>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#f5f0eb', marginBottom: '12px' }}>{f.title}</p>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', fontWeight: 300, color: '#4a4a4a', lineHeight: 1.8 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── How it works ── */}
        <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: '64px', marginBottom: '64px' }}>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '16px', textAlign: 'center' }}>How It Works</p>
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 300, color: '#f5f0eb', textAlign: 'center', marginBottom: '56px', lineHeight: 1.1 }}>
            Up and running in<br /><em style={{ color: '#c9a96e' }}>three steps</em>
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '32px' : '48px' }}>
            {[
              { step: '01', title: 'Tell us your taste', desc: 'A short onboarding — your experience level, the scent families you love, your budget and occasions. Takes two minutes.' },
              { step: '02', title: 'Add your collection', desc: 'Log the bottles you own with their size and current level. Flakon uses this to make recommendations from what you already have.' },
              { step: '03', title: 'Get your daily scent', desc: "Every morning, open Flakon and see what to wear today. Weather-aware, occasion-perfect, pulled from your own collection." },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: isMobile ? 'left' : 'center' }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '3rem', fontWeight: 300, color: '#1e1e1e', lineHeight: 1, marginBottom: '16px' }}>{s.step}</p>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#f5f0eb', marginBottom: '12px' }}>{s.title}</p>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', fontWeight: 300, color: '#4a4a4a', lineHeight: 1.8 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Final CTA ── */}
        <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: '64px', textAlign: 'center', marginBottom: '64px' }}>
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '2.8rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '24px', lineHeight: 1.1 }}>
            Ready to wear the right<br /><em style={{ color: '#c9a96e' }}>scent every day?</em>
          </p>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.82rem', fontWeight: 300, color: '#4a4a4a', marginBottom: '40px', lineHeight: 1.8 }}>Free to use. No credit card required.</p>
          <button style={styles.primaryBtn} onClick={onSignUp}>Begin Your Collection</button>
        </div>

        {/* ── Footer ── */}
        <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', color: '#2a2a2a' }}>© {new Date().getFullYear()} Flakon®</p>
          <div style={{ display: 'flex', gap: '24px' }}>
            <a href="/legal" style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#3a3a3a', textDecoration: 'none' }}>Privacy Policy</a>
            <a href="/legal" style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#3a3a3a', textDecoration: 'none' }}>Data Retention</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;